import type { Cancel } from './timer.js';

/**
 * Whether the player currently holds the screen (dwf-063).
 *
 * The player advances its loop and queues a `kind: 'play'` record for every item it
 * finishes. A backgrounded WebView keeps doing both — the `<video>` element ends, the
 * timer fires — and the resulting rows are true statements about a DOM element and false
 * statements about a television. They are also permanent: `POST /device/events` inserts
 * with `on_conflict_do_nothing` and there is no delete path anywhere in the API, so a
 * play billed for a frame nobody saw is the number for ever (invariant 4, D64).
 *
 * decision-log **D211** makes that window routine rather than rare: the O167 waiver
 * accepts a drop to Amazon's launcher for several seconds on a HOME press, and the
 * watchdog's own recovery budget is `BRING_TO_FRONT_VERIFY_MS = 6_000`.
 *
 * This is the seam the player watches. It is an interface rather than a direct
 * `document.addEventListener` for the same reason every timer goes through `Timer`: the
 * headless harness has to be able to hide a screen for a simulated hour without a DOM.
 */
export interface VisibilitySource {
  /** True while nothing on this screen can be seen. */
  hidden(): boolean;
  /** Called on every *transition*; the argument is the new hidden state. */
  subscribe(listener: (hidden: boolean) => void): Cancel;
}

/**
 * The default for any host that cannot answer the question.
 *
 * "Cannot tell" must read as visible, never as hidden: a source that wrongly reports
 * hidden parks the loop and puts a stale frame on a customer-facing TV for ever, which
 * is an invariant 9 failure. Over-counting is the bug this feature fixes; a dark screen
 * is the bug it must not cause.
 */
export const ALWAYS_VISIBLE: VisibilitySource = {
  hidden: () => false,
  subscribe: () => () => {
    // Nothing to unsubscribe from: this source never changes.
  },
};

/** The slice of `Document` this needs, so the harness can pass a fake and tests can too. */
export interface VisibilityDocument {
  readonly hidden?: boolean;
  readonly visibilityState?: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

/**
 * The real one: `document.visibilitychange`.
 *
 * On Fire OS this fires when the activity's window goes away under a HOME press, and
 * again when the watchdog brings it back. In a browser tab for QA and on any Android TV
 * stick without our shell it is the *only* signal — `MainActivity.onPause()`'s
 * `webView?.onPause()` is the other half of the belt-and-braces pair and does not exist
 * off Fire OS.
 */
export function createDocumentVisibility(doc: VisibilityDocument): VisibilitySource {
  const hidden = (): boolean =>
    typeof doc.visibilityState === 'string' ? doc.visibilityState === 'hidden' : doc.hidden === true;

  return {
    hidden,
    subscribe: (listener) => {
      const onChange = (): void => {
        listener(hidden());
      };
      doc.addEventListener('visibilitychange', onChange);
      return () => {
        doc.removeEventListener('visibilitychange', onChange);
      };
    },
  };
}

/** A source the harness (and any test) drives by hand. */
export interface ControllableVisibility extends VisibilitySource {
  set(hidden: boolean): void;
}

export function createControllableVisibility(initiallyHidden = false): ControllableVisibility {
  let hidden = initiallyHidden;
  const listeners = new Set<(hidden: boolean) => void>();
  return {
    hidden: () => hidden,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    set: (next) => {
      if (next === hidden) return;
      hidden = next;
      for (const listener of listeners) listener(next);
    },
  };
}
